import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    MyScheduleContextProps,
    MyScheduleContextValues,
} from "../types/contexts";
import { SortColumns } from "../types/myschedule";
import { IGame } from "../../../../types/api";
import { SortOrders, UmpirePay } from "../../../../types/shared";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import {
    selectAllSettings,
    selectAllSites,
} from "../../../../features/global/globalSlice";

const MyScheduleContext = createContext<MyScheduleContextValues | null>(null);

export const useMyScheduleContext = () => {
    const context = useContext(MyScheduleContext);
    if (!context) {
        throw new Error(
            "useMyScheduleContext must be used within a MyScheduleProvider",
        );
    }
    return context;
};

export const MyScheduleProvider: React.FC<MyScheduleContextProps> = props => {
    const { children, initialGames, schedule } = props;
    const settings = useAppSelector(selectAllSettings);
    const sites = useAppSelector(selectAllSites);
    const [games, setGames] = useState<IGame[]>([]);
    const [sortColumn, setSortColumn] = useState<SortColumns>("date");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");
    const [umpirePay, setUmpirePay] = useState<Record<number, UmpirePay[]>>({});

    useEffect(() => {
        setGames(initialGames);
    }, [initialGames]);

    useEffect(() => {
        const pay: typeof umpirePay = {};
        settings.map(s => {
            const match = s.settingName.match(/_umpire_pay/);
            if (match) {
                pay[s.site] = s.setting;
            }
        });

        setUmpirePay(pay);
    }, []);

    const sortedGames = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...games].sort((a, b) => {
            switch (sortColumn) {
                case "age":
                    aValue = parseInt(a.ageLevel.ageLevel);
                    bValue = parseInt(b.ageLevel.ageLevel);
                    break;
                case "location":
                    aValue = a.fieldName;
                    bValue = b.fieldName;
                    break;
                case "date":
                    aValue = new Date(a.date).getTime();
                    bValue = new Date(b.date).getTime();
                    break;
                case "site":
                    const aSite = sites.find(s => s.id === a.site);
                    const bSite = sites.find(s => s.id === b.site);
                    aValue = aSite?.displayName;
                    bValue = bSite?.displayName;
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [games, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            games: sortedGames,
            setGames,
            totalItems: sortedGames.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            umpirePay,
            schedule,
        }),
        [sortedGames, umpirePay],
    );

    return (
        <MyScheduleContext.Provider value={contextValue}>
            {children}
        </MyScheduleContext.Provider>
    );
};
